import Carousel from "@/components/carousel";
import HateCircular from "@/components/hate-circular";
import useTranslation from "@/hooks/use-translation";
import { Media, NormalizedContent } from "@/models/document";
import {
  HiOutlineHeart,
  HiOutlineChatBubbleLeft,
  HiOutlineShare,
  HiOutlineUsers,
  HiOutlineUserPlus,
  HiOutlineNewspaper,
  HiOutlineMagnifyingGlassPlus,
} from "react-icons/hi2";
import { Card, CardBody, Tooltip, IconButton } from "@/components/ui";
import React, { useState } from "react";
import NormalizeMedia from "../normalizeMedia";
import PostDetailModal from "@/components/modals/post-detail-modal";
import { safeString } from "@/utils/safe-value";

type EmbedProps = {
  normalized: NormalizedContent;
  sentiment?: number;
  actions?: React.ReactNode;
};

function NormalizeEmbed({ normalized, sentiment, actions }: EmbedProps) {
  const { t } = useTranslation();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Adapter les données brutes au format NormalizedContent
  // Use safeString to handle cases where fields might be objects with {value: ...}
  const rawData = normalized as any;
  const text = safeString(rawData.description || rawData.text || rawData.title);
  const title = safeString(rawData.title) || (text ? text.substring(0, 100) + "..." : "");
  const adaptedData = rawData.normalized
    ? rawData
    : {
        id: rawData.id,
        text,
        title,
        url: safeString(rawData.url),
        date: safeString(rawData.date || rawData.published_time),
        language: safeString(rawData.lang || rawData.language),
        author: rawData.author
          ? {
              id: rawData.author.id,
              username: safeString(rawData.author.username),
              title: safeString(rawData.author.nickname || rawData.author.name),
              description: safeString(rawData.author.signature || rawData.author.bio),
              url: safeString(rawData.author.url),
              verified: rawData.author.verified,
              stats: rawData.author.stats,
              text: safeString(rawData.author.signature || rawData.author.bio),
              media: rawData.author.avatar
                ? [
                    {
                      id: "avatar",
                      type: "image",
                      url: safeString(rawData.author.avatar),
                      thumbnail: safeString(rawData.author.avatar),
                    },
                  ]
                : [],
            }
          : undefined,
        media:
          rawData.image || rawData.thumbnail
            ? [
                {
                  id: "media",
                  type: rawData.play_url ? "video" : "image",
                  url: safeString(rawData.play_url || rawData.video_url),
                  thumbnail: safeString(rawData.image || rawData.thumbnail),
                  description: safeString(rawData.description),
                },
              ]
            : [],
        stats: rawData.stats,
      };

  const returnCardMedia = (media: Media[]) => {
    if (media.length === 0) return null;

    return <NormalizeMedia media={media[0]} size="small" />;
  };

  const returnStatsIcon = (icon: string) => {
    const iconClass = "w-4 h-4 text-content-tertiary";
    switch (icon) {
      case "likes":
        return (
          <Tooltip content={t("likes")}>
            <HiOutlineHeart className={iconClass} />
          </Tooltip>
        );
      case "comments":
        return (
          <Tooltip content={t("comments")}>
            <HiOutlineChatBubbleLeft className={iconClass} />
          </Tooltip>
        );
      case "shares":
        return (
          <Tooltip content={t("shares")}>
            <HiOutlineShare className={iconClass} />
          </Tooltip>
        );
      case "followers":
        return (
          <Tooltip content={t("followers")}>
            <HiOutlineUsers className={iconClass} />
          </Tooltip>
        );
      case "following":
        return (
          <Tooltip content={t("following")}>
            <HiOutlineUserPlus className={iconClass} />
          </Tooltip>
        );
      case "posts":
        return (
          <Tooltip content={t("posts")}>
            <HiOutlineNewspaper className={iconClass} />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const cardContent = (
    <Card 
      variant="outlined" 
      hover 
      className="h-full flex flex-col"
    >
      <CardBody className="p-0 flex flex-col h-full">
        {/* Header avec auteur */}
        <div className="flex items-start justify-between p-4 pb-2">
          {adaptedData.author && (
            <div className="flex items-start space-x-3 flex-1 min-w-0">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-arc-border-subtle">
                  {returnCardMedia(adaptedData.author.media ?? [])}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                  <span className="font-semibold text-content-primary truncate text-sm sm:text-base">
                    {adaptedData.author?.username ?? ""}
                  </span>
                  {adaptedData.author.title && (
                    <span className="text-content-tertiary truncate text-xs sm:text-sm">
                      {adaptedData.author.title}
                    </span>
                  )}
                  {adaptedData.author?.verified && (
                    <span className="text-info text-xs sm:text-sm">
                      ✓
                    </span>
                  )}
                </div>
                {adaptedData.author?.description && (
                  <p className="text-content-tertiary line-clamp-2 text-xs">
                    {adaptedData.author.description}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <Tooltip content={t("view_details")}>
              <IconButton
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDetailModalOpen(true);
                }}
                className="text-content-tertiary hover:text-accent"
              >
                <HiOutlineMagnifyingGlassPlus className="w-5 h-5" />
              </IconButton>
            </Tooltip>
            {sentiment && <HateCircular sentiment={sentiment} />}
            {actions}
          </div>
        </div>

        {/* Contenu principal */}
        <div className="px-4 pb-3 flex-1">
          {adaptedData?.title && (
            <h3 className="font-semibold text-content-primary mb-2 line-clamp-2 text-sm sm:text-base">
              {adaptedData.title}
            </h3>
          )}
          {adaptedData.text && (
            <p className="text-content-secondary leading-relaxed line-clamp-3 text-xs sm:text-sm">
              {adaptedData.text}
            </p>
          )}
        </div>

        {/* Média */}
        {adaptedData.media && adaptedData.media.length > 0 && (
          <div className="px-4 pb-3">
            <div className="relative rounded-lg overflow-hidden bg-surface-secondary h-32 sm:h-40">
              <Carousel
                items={adaptedData.media.map((media: any, index: number) => {
                  return (
                    <NormalizeMedia key={index} media={media} size="small" />
                  );
                })}
              />
            </div>
          </div>
        )}

        {/* Statistiques */}
        {adaptedData.stats && (
          <div className="px-4 pb-3">
            <div className="flex flex-wrap gap-2 sm:gap-4">
              {Object.entries(adaptedData.stats).map(([key, value]) => {
                if (value === undefined || value === null) return null;
                return (
                  <div key={key} className="flex items-center space-x-1">
                    {returnStatsIcon(key)}
                    <span className="text-content-secondary font-medium text-xs sm:text-sm">
                      {typeof value === "number"
                        ? value.toLocaleString()
                        : String(value)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer avec date */}
        <div className="px-4 py-3 bg-surface-secondary border-t border-arc-border-subtle mt-auto">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-1 sm:space-y-0">
            {adaptedData.date && (
              <Tooltip content={t("postedon")}>
                <span className="text-content-tertiary text-xs">
                  {new Date(adaptedData.date).toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </Tooltip>
            )}
            <div className="flex items-center space-x-1 text-content-tertiary">
              <span className="text-xs">
                {adaptedData.language && adaptedData.language.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  return (
    <div className="w-full max-w-full">
      {adaptedData.url ? (
        <a
          href={adaptedData.url}
          target="_blank"
          rel="noopener noreferrer"
          className="no-underline"
        >
          {cardContent}
        </a>
      ) : (
        cardContent
      )}

      {/* Modal de détails */}
      <PostDetailModal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        content={normalized as any}
        sentiment={sentiment}
      />
    </div>
  );
}

export default NormalizeEmbed;
